import logger from '@elab/core/utils/logger';
import { paymentSlots } from '@elab/core/utils/constants';

export const KLARNA_CONTAINER_ID = 'klarna-payments-container';

export function klarnaMethodIsLoaded(methods) {
    return methods.map(method => {
        if (method.code.includes(paymentSlots.KLARNA)) {
            return true;
        }
    });
}

export function klarnaInitLoadWidget(sessionData) {
    return new Promise((resolve, reject) => {
        if (
            window.Klarna &&
            sessionData.clientToken &&
            Array.isArray(sessionData.paymentCategories)
        ) {
            const paymentMethodCategory =
                sessionData.paymentCategories.length && sessionData.paymentCategories[0].identifier;
            try {
                window.Klarna.Payments.init({
                    client_token: sessionData.clientToken,
                });
                window.Klarna.Payments.load(
                    {
                        container: `#${KLARNA_CONTAINER_ID}`,
                        payment_method_category: paymentMethodCategory,
                    },
                    function(res) {
                        resolve(res && res.show_form);
                    },
                );
            } catch (e) {
                logger.error(e);
                reject(false);
            }
        } else {
            reject(false);
        }
    });
}

export function klarnaAuthorize(sessionData, cartData) {
    return new Promise((resolve, reject) => {
        if (
            window.Klarna &&
            sessionData.clientToken &&
            Array.isArray(sessionData.paymentCategories)
        ) {
            const paymentMethodCategory =
                sessionData.paymentCategories.length && sessionData.paymentCategories[0].identifier;
            window.Klarna.Payments.authorize(
                {
                    payment_method_category: paymentMethodCategory,
                },
                klarnaCombineAuthorizeData(sessionData, cartData),
                function(res) {
                    resolve(res);
                },
            );
        } else {
            reject(false);
        }
    });
}

function klarnaCombineAuthorizeData(sessionData, cartData) {
    // Klarna uses amounts in minor units according to the ISO 4217 standard
    // https://docs.klarna.com/klarna-payments/api/payments-api/#tag/Data-Types
    const priceMultiplier = 100;

    const orderLines = cartData.entries.map(entry => {
        const domainProtocol = window.location.protocol;
        const domainHost = window.location.host;
        const domainPort = `${window.location.port ? ':' : ''}${window.location.port}`;
        const domainUrl = `${domainProtocol}//${domainHost}${domainPort}`;
        const name = [
            (entry.product.masterBrand && entry.product.masterBrand.name) || ' ',
            entry.product.rangeName || ' ',
            entry.product.name || ' ',
        ];
        const image =
            entry.product.images && entry.product.images[0] && entry.product.images[0].url;
        return {
            type: 'physical',
            reference: entry.product.code,
            name: name
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim(),
            quantity: entry.quantity,
            unit_price: Math.round(entry.basePrice.value * priceMultiplier),
            total_amount: Math.round(entry.totalPrice.value * priceMultiplier),
            total_discount_amount: Math.round(entry.totalDiscount.value * priceMultiplier),
            tax_rate: 0,
            total_tax_amount: 0,
            product_url: `${domainUrl}${entry.product.url.length ? entry.product.url : '/'}`,
            image_url: `${domainUrl}${!image ? cartData.imagePlaceholderPath : image}`,
        };
    });

    if (cartData.deliveryMode.name) {
        if (cartData.deliveryMode.deliveryCost && cartData.deliveryMode.deliveryCost.value) {
            const deliveryCost = Math.round(
                cartData.deliveryMode.deliveryCost.value * priceMultiplier,
            );
            if (deliveryCost > 0) {
                orderLines.push({
                    name: sessionData.deliveryLineName.replace('{0}', cartData.deliveryMode.name),
                    quantity: 1,
                    unit_price: deliveryCost,
                    total_amount: deliveryCost,
                });
            }
        }
    }

    if (cartData.giftOptionsTotal && cartData.giftOptionsTotal.optionsTotalPrice) {
        const giftOptionsCost = Math.round(
            cartData.giftOptionsTotal.optionsTotalPrice.value * priceMultiplier,
        );
        if (giftOptionsCost > 0) {
            orderLines.push({
                name: 'GiftWrap Option cost',
                quantity: 1,
                unit_price: giftOptionsCost,
                total_amount: giftOptionsCost,
            });
        }
    }

    if (cartData.amountToPayValue < cartData.totalPrice.value) {
        const giftVouchersCost = Math.round(
            cartData.amountToPayValue * priceMultiplier -
                cartData.totalPrice.value * priceMultiplier,
        );
        if (giftVouchersCost < 0) {
            orderLines.push({
                name: 'GiftVoucher amount',
                quantity: 1,
                unit_price: giftVouchersCost,
                total_amount: giftVouchersCost,
            });
        }
    }

    const klarnaData = {
        purchase_country: cartData.purchaseCountry,
        purchase_currency: cartData.purchaseCurrency,
        locale: cartData.purchaseLocale.replace(/_/, '-'),
        billing_address: {
            given_name: cartData.paymentAddress.firstName,
            family_name: cartData.paymentAddress.lastName,
            email: cartData.paymentAddress.email,
            title: cartData.paymentAddress.title,
            street_address: cartData.paymentAddress.line1,
            street_address2: cartData.paymentAddress.line2,
            postal_code: cartData.paymentAddress.postalCode,
            city: cartData.paymentAddress.town,
            region: cartData.paymentAddress.province,
            phone: cartData.paymentAddress.mobileNumber,
            country: cartData.paymentAddress.country.isocode,
        },
        order_amount: Math.round(cartData.amountToPayValue * priceMultiplier),
        order_tax_amount: 0,
        order_lines: orderLines,
        customer: {
            date_of_birth: cartData.dateOfBirth,
        },
    };

    if (!cartData.isDeliveryModePickup) {
        klarnaData.shipping_address = {
            given_name: cartData.deliveryAddress.firstName,
            family_name: cartData.deliveryAddress.lastName,
            email: cartData.deliveryAddress.email,
            title: cartData.deliveryAddress.title,
            street_address: cartData.deliveryAddress.line1,
            street_address2: cartData.deliveryAddress.line2,
            postal_code: cartData.deliveryAddress.postalCode,
            city: cartData.deliveryAddress.town,
            region: cartData.deliveryAddress.province,
            phone: cartData.deliveryAddress.mobileNumber,
            country: cartData.deliveryAddress.country.isocode,
        };
    }

    return klarnaData;
}
